import { useTranslation } from "react-i18next";
import type { ConsentTopic, TopicKey } from "../../api/consent";
import { openConsentPage } from "./ConsentChecklist";
import ConsentGateList from "./ConsentGateList";

/**
 * We updated our terms (Figma "Web / Privacy / We updated our terms / Modal" 4435:3846 / 4435:3951, Dark 4436:4944 /
 * 4436:5046): a material new version of a topic answered before. Only the changed topics, in one list with one
 * confirm (source RECONSENT); "What changed" lists each changed document with its summary and a link to its page.
 * Decline and sign out.
 */
export default function ReconsentModal({
  required,
  changed,
  onConfirm,
  onDecline,
}: {
  required: ConsentTopic[];
  changed: ConsentTopic[];
  onConfirm: (ticked: Set<TopicKey>) => Promise<void>;
  onDecline: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  // Only the privacy notice changed: nothing to accept, so the title and text name the notice, not "terms".
  const noticeOnly = !required.some((x) => x.key === "terms");
  const intro = (
    <div
      data-testid="consent-reconsent-changes"
      className="mt-3 rounded-xl bg-[var(--bg-muted,var(--muted))] p-3 text-sm text-[var(--foreground)]"
    >
      <div className="font-semibold">{t("consent_what_changed")}</div>
      <ul className="m-0 mt-1 list-none p-0">
        {required.map((x) => (
          <li key={x.key} data-consent-changed={x.key} className="mt-1">
            <span className="font-medium">{x.title}</span>
            {x.summary && <span className="text-[var(--foreground-muted)]"> — {x.summary}</span>}{" "}
            {x.url && (
              <button
                type="button"
                data-testid={`consent-reconsent-read-${x.key}`}
                onClick={() => openConsentPage(x)}
                className="border-none bg-transparent p-0 text-sm text-[var(--primary-text)] underline cursor-pointer"
              >
                {t("consent_more")}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <ConsentGateList
      testId="consent-reconsent"
      title={noticeOnly ? t("consent_reconsent_title_notice") : t("consent_reconsent_title")}
      text={noticeOnly ? t("consent_reconsent_text_notice") : t("consent_reconsent_text")}
      intro={intro}
      required={required}
      optional={changed}
      optionalTitle={t("consent_group_changed")}
      termsKeys={["consent_reconsent_terms_before", "consent_bys_terms_link", "consent_bys_terms_after"]}
      declineLabel={t("consent_decline_sign_out")}
      onConfirm={onConfirm}
      onDecline={onDecline}
    />
  );
}
